import Decimal from 'decimal.js';
import { isDateAfter, isDateBefore, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { eventType } from 'src/service-worker/offline_handlers/aol/enums';
import { CargoInTankItem, CargoTempCategory } from 'src/types/ViewModel/CargoTempLog/CargoInTankItem';
import { CargoTemperatureLogDate } from 'src/types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { getFormattedCargoNumber } from '../stowage/cargoOperationUtils';
import { CargoTemperatureConfig } from 'src/types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';

export const getCargoTempCategory = (cargo: RotationCargo): CargoTempCategory => {
    if (!cargo || !cargo.product) return CargoTempCategory.NonHeat;

    // Check for inhibited cargo
    if (cargo.product.category?.isInhibited) return CargoTempCategory.Inhibited;

    // Check for heat sensitive cargo
    if (cargo.product.category?.shallNotBeExposedToExcessiveHeat) return CargoTempCategory.HeatSensitive;

    // Check for heated cargo
    const minTemperature = cargo.product.voyageTemperatureRange?.min;
    const MIN_TEMPERATURE_NA_DEFINITION = 0;
    if (minTemperature != null && minTemperature > MIN_TEMPERATURE_NA_DEFINITION) return CargoTempCategory.HeatedCooled;

    // Check for cooled cargo
    const maxTemperature = cargo.product.voyageTemperatureRange?.max;
    const MAX_TEMPERATURE_NA_DEFINITION = 35;
    if (maxTemperature != null && maxTemperature < MAX_TEMPERATURE_NA_DEFINITION) return CargoTempCategory.HeatedCooled;

    return CargoTempCategory.NonHeat;
};

export const populateCargoOnBoardFromStowage = (
    cargoTempLogDates: CargoTemperatureLogDate[],
    activeMasterPlan: StowagePlan,
    stowageIntegrationFromDate: Date,
    events: TankersEvent[],
    vessel: Vessel
): CargoTemperatureLogDate[] => {
    if (!activeMasterPlan || !activeMasterPlan.cargoes || !activeMasterPlan.stowageItems || !vessel) return cargoTempLogDates;

    const fromDate = stowageIntegrationFromDate;
    const firstVoyage = vessel.firstVoyageOrcaWebStowageImosSync;

    if (!firstVoyage || firstVoyage <= 0) {
        // If first voyage is not set, we cannot determine cargo on board
        return cargoTempLogDates;
    }

    cargoTempLogDates.forEach((cargoTempLogDate) => {
        const cargoInTankItems: CargoInTankItem[] = [];
        if (!isDateAfter(cargoTempLogDate.logDate, fromDate)) return;
        const midnightAfterLogDate = new Date(cargoTempLogDate.logDate);
        midnightAfterLogDate.setHours(24);

        // find all loading stowage items that have a pumping commence event before midnight after the log date
        const loadingStowageItemsUntilToday = activeMasterPlan.stowageItems.filter((stowage) => {
            return (
                +stowage?.cargoOperation?.cargo?.voyageNumber >= firstVoyage &&
                events.some(
                    (event) =>
                        event.eventTypeId === eventType.pumpingCommence &&
                        event.cargoOperationId === stowage.cargoOperation.imosCargoOperationId &&
                        stowage.cargoOperation.operationType === 'Loading' &&
                        isDateSameOrBefore(event.eventDate.actualDateTimeLocal, midnightAfterLogDate)
                )
            );
        });
        // get distinct cargoes possibly on board at date from the stowage items
        const loadedCargoes = loadingStowageItemsUntilToday.reduce((acc, stowageItem) => {
            const cargo = activeMasterPlan.cargoes.find((cargo) => cargo.cargoGuid === stowageItem.cargoOperation.cargoGuid);
            if (cargo && !acc.some((c) => c.cargoGuid === cargo.cargoGuid)) {
                acc.push(cargo);
            }
            return acc;
        }, [] as Array<RotationCargo>);

        loadedCargoes.forEach((cargo) => {
            const loadingStowage = loadingStowageItemsUntilToday.filter((stowage) => {
                return stowage.cargoOperation.cargoGuid === cargo.cargoGuid;
            });
            const tanks = loadingStowage.map((stowage) => stowage.tankId);

            const tanksNotUnloaded = tanks.filter((tank) => {
                const unloadingStowage = activeMasterPlan.stowageItems.filter((stowage) => {
                    return (
                        stowage.cargoOperation.cargoGuid === cargo.cargoGuid &&
                        stowage.tankId === tank &&
                        stowage.cargoOperation.operationType === 'Unloading' &&
                        events.some(
                            (event) =>
                                event.eventTypeId === eventType.pumpingComplete &&
                                event.cargoOperationId === stowage.cargoOperation.imosCargoOperationId &&
                                isDateBefore(event.eventDate.actualDateTimeLocal, cargoTempLogDate.logDate)
                        )
                    );
                });

                // No unloading stowage before today means the cargo is still on board in the tank
                if (unloadingStowage.length === 0) return true;

                const loadedQuantity = loadingStowage
                    .filter((stowage) => stowage.tankId === tank)
                    .reduce((acc, stowage) => {
                        const quantity = stowage.weight ?? 0;
                        return acc.add(new Decimal(quantity));
                    }, new Decimal(0));
                const unloadedQuantity = unloadingStowage.reduce((acc, stowage) => {
                    const quantity = stowage.weight ?? 0;
                    return acc.add(new Decimal(quantity));
                }, new Decimal(0));

                // check if remaining quantity is greater than 0 with a small margin of error
                const marginOfError = 0.01;
                // add load and unload because unloading is negative
                const remainingQuantity = loadedQuantity.add(unloadedQuantity).toNumber();
                if (remainingQuantity > marginOfError) return true;

                return false;
            });
            if (tanksNotUnloaded.length === 0) return null;

            cargoInTankItems.push(
                ...tanksNotUnloaded.map((tankId) => {
                    return {
                        cargoNumber: getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, false),
                        isOnBoard: true,
                        tankId: tankId,
                        tradeName: cargo.product.tradeName,
                        cargoTempCategory: getCargoTempCategory(cargo),
                    } as CargoInTankItem;
                })
            );
        });
        cargoTempLogDate.cargoInTankList = cargoInTankItems;
    });
    return cargoTempLogDates;
};

export const isInhibitorQuestionnaireCompleted = (config: CargoTemperatureConfig): boolean => {
    let isValid = true;
    if (
        config.inhibitorType == null ||
        config.inhibitorAmount == null ||
        config.inhibitorAddedDate == null ||
        config.inhibitorEffectivenessEntries == null ||
        config.inhibitorEffectivenessEntries?.length === 0 ||
        !config.inhibitorEffectivenessEntries.some((entry) => entry.days != null && entry.temperature != null && entry.days > 0) ||
        !config.inhibitorExpiryAction
    )
        isValid = false;

    if (config.isOxygenDependent && (config.minimumOxygenLevel == null || config.minimumOxygenLevel <= 0)) isValid = false;
    return isValid;
};
